import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { ChevronRight as ChevronRightIcon, type SvgIconComponent } from "@mui/icons-material";

interface LinkedEntityRowProps {
  /** Resource icon (e.g. CorporateFareIcon for clients). */
  icon: SvgIconComponent;
  /** Primary label — entity name. */
  label: string;
  /** Optional secondary line below the label (e.g. state, vendor type). */
  sublabel?: string;
  /** Internal route to the entity's show page (e.g. `/clients/abc-123/show`). */
  to: string;
  /** Loading state — renders a skeleton row in place of the link. */
  isLoading?: boolean;
}

/**
 * Sidebar list-row primitive used by `DocumentDetailsDrawer` to surface a
 * linked entity (client / vendor / policy) with click-through navigation.
 *
 * Row anatomy: leading icon · primary label (+ optional sublabel below) ·
 * trailing chevron. The whole row is clickable. Replaces the earlier chip
 * primitive that didn't read well in a vertical stack.
 */
export const LinkedEntityRow = ({ icon: Icon, label, sublabel, to, isLoading }: LinkedEntityRowProps) => {
  if (isLoading) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
        <Skeleton variant="circular" width={20} height={20} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="40%" height={16} />
        </Box>
      </Stack>
    );
  }

  return (
    <Box
      component={RouterLink}
      to={to}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1,
        px: 1,
        mx: -1,
        borderRadius: 1,
        textDecoration: "none",
        color: "inherit",
        cursor: "pointer",
        transition: "background-color 120ms ease",
        "&:hover": {
          bgcolor: "action.hover",
        },
        "&:focus-visible": {
          outline: 2,
          outlineColor: "primary.main",
          outlineOffset: -2,
        },
      }}
    >
      <Icon sx={{ fontSize: 20, color: "text.secondary", flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={500} noWrap title={label}>
          {label}
        </Typography>
        {sublabel && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {sublabel}
          </Typography>
        )}
      </Box>
      <ChevronRightIcon sx={{ fontSize: 18, color: "text.disabled", flexShrink: 0 }} />
    </Box>
  );
};
